
  import { Component, Vue }               from 'vue-property-decorator';
  import { Action, Getter }               from 'vuex-class';
  import { EventBus,
           ResponsiveUiHelper,
           ConfigService,
           ConfigThemeService,
           BotInitializationService }     from '../..';
  import { NotificationEntity,
           SessionEntity }                from '../..';
  import   ChatboxPanelComponent          from '../messages/ChatboxPanelComponent.vue';

  @Component({
    name: 'FloatingWidgetComponent',
    components: {
      ChatboxPanelComponent,
    }
  })
  export default class FloatingWidgetComponent extends Vue {
    @Action('setConversationPanelState', { namespace: 'conversationSession' }) setConversationPanelState: any;
    @Action('markNotificationAsSeen', { namespace: 'messages' }) markNotificationAsSeen: any;
    @Getter('getUnseenNotifications', { namespace: 'messages' }) getUnseenNotifications!: NotificationEntity[];
    @Getter('isPresentationAvatarAllowed', { namespace: 'conversationSession' }) isPresentationAvatarAllowed!: boolean;
    @Getter('getNotificationsToShow', { namespace: 'messages' }) getNotificationsToShow!: NotificationEntity[];
    @Getter('conversationSessionInfo', { namespace: 'conversationSession' }) conversationSessionInfo!: SessionEntity | undefined;

    playWelcomeAvatarBounceAnimation: boolean = false;
    playWelcomeAvatarSlideAnimation: boolean = false;
    playWelcomeAvatarFadeAnimation: boolean = false;

    isFullscreenDisplay: boolean = false;
    isDrawerDisplayed: boolean = false;
    panelIsOpen: boolean = false;

    showReduceButton: boolean = false; // show or hide the reduce button
    isReduced: boolean = false; // avatar reduced state
    isMobile: boolean = false;  // mobile device detection

    get computedTabindex() {
      return this.panelIsOpen ? -1 : 0;
    }

    get conversationPanelAnimation() {
      return ConfigThemeService.getChatboxPanelAnimation();
    }

    get avatarUrl() {
      return ConfigService.getBotImagePresentation();
    }

    get avatarHeight() {
      let containerHeightValue: number = Number(ConfigThemeService.getPresentationAvatarHeight());
      if (containerHeightValue < 40 || isNaN(containerHeightValue)) {
        containerHeightValue = 100;
      }
      // if mobile or reduced, we reduce the height
      const reducedHeight: number = this.isMobile || this.isReduced ? containerHeightValue*this.reducedAvatarRatio : containerHeightValue;
      return `${reducedHeight}px`;
    }

    get avatarWidth() {
      let containerWidthValue: number = Number(ConfigThemeService.getPresentationAvatarWidth());
      if (containerWidthValue < 40 || isNaN(containerWidthValue)) {
        containerWidthValue = 100;
      }
      // if mobile or reduced, we reduce the width
      const reducedWidth: number = this.isMobile || this.isReduced ? containerWidthValue*this.reducedAvatarRatio : containerWidthValue;
      return `${reducedWidth}px`;
    }

    get avatarMargin() {
       const containerMarginValue: number = Number(ConfigThemeService.getPresentationAvatarMargin());
       return (!isNaN(containerMarginValue)) ? `${containerMarginValue}px` : '16px';
    }
    
    get welcomeBannerPadding() {
      const containerPaddingValue: number = Number(ConfigThemeService.getWelcomeBannerPadding());
      return (!isNaN(containerPaddingValue)) ? `${containerPaddingValue}px` : '10px';
    }

    get welcomeBannerMaxHeight() {
      // 56px refers to 2 * 10px padding from the welcome banner + 2 * 16px margin (top and bottom) from the chatbot avatar presentation  
      const computedMaxHeight: string = 'calc(100vh - '                                                               // Screen height -
                                              + this.avatarHeight + ' - '                                             // the avatar's height -
                                              + this.avatarMargin + ' - ' + this.avatarMargin + ' - '                 // the avatar's margin (top and bottom) -
                                              + this.welcomeBannerPadding + ' - ' + this.welcomeBannerPadding + ')' ; // the banner's padding (top and bottom)
      return {maxHeight: computedMaxHeight}
    }

    get reducedAvatarIcon() {
      return ConfigThemeService.getIconReducedAvatar();
    }

    get reducedAvatarRatio() {
      const ratio = ConfigThemeService.getReducedRatio();
      return ratio;
    }

    get reducedAvatarTop() {
      return `${ConfigThemeService.getReducedIconTopMargin()}px`;
    }

    get reducedAvatarRight() {
      return `${ConfigThemeService.getReducedIconRightMargin()}px`;
    }

    get reducedAvatarPlatfom() {
      return ConfigThemeService.getReducedPlatform();
    }

    get rootContainerStyles() {
      if (!this.isPresentationAvatarAllowed) {
        return ConfigThemeService.getFloatingModeStyles(this.$vuetify);
      }
      return {};
    }

    get avatarImgStyles() {
      if (this.isPresentationAvatarAllowed) {
        return ConfigThemeService.getPresentationAvatarStyles(this.$vuetify);
      }
      return { margin: "16px"};
    }

    get botWelcomeBannerStyles() {
      if (this.isPresentationAvatarAllowed) {
        return ConfigThemeService.getBotWelcomeBannerStyles(this.$vuetify);
      }
      return {};
    }

    get botWelcomeBannerMessage() {
      return ConfigService.getWelcomeBannerMessage();
    }
    
    get showWelcomeBanner() {
      if (this.isFullscreenDisplay && ConfigService.showWelcomeBannerMessage()) {
        return false;
      } else {
        return true;
      }
    }

    isWidgetOnFullscreen() {
      return (this.isFullscreenDisplay && !this.isPresentationAvatarAllowed ? true 
                                                                            : false);
    }

    get notificationBadgeStyle() {
      const calculNotificationSize =  this.isReduced ? `${20 * this.reducedAvatarRatio}px` : '20px';
      const left = parseInt(ConfigThemeService.getNotificationBadgeLeft());
      const top = parseInt(ConfigThemeService.getNotificationBadgeTop());
      return { 
          backgroundColor: ConfigThemeService.getNotificationBadgeColor(this.$vuetify),
          color: ConfigThemeService.getNotificationFontColor(this.$vuetify),
          height: calculNotificationSize,
          width: calculNotificationSize,
          fontSize: this.isReduced ? `${14 * this.reducedAvatarRatio}px` : '20px',
          left : this.isReduced ? `${left * (this.reducedAvatarRatio*1.3)}px` : `${left}px`,
          top : this.isReduced ? `${top * this.reducedAvatarRatio}px` : `${top}px`,
      };
    }
    
    created() {
      EventBus.$on('ON_DRAWER_OPEN', () => this.isDrawerDisplayed = true);
      EventBus.$on('ON_DRAWER_CLOSE', () => this.isDrawerDisplayed = false);
      EventBus.$on('ON_RESIZE_SCREEN_MODE', (isFullscreen: boolean) => {
        this.isFullscreenDisplay = isFullscreen;        
        BotInitializationService.toggleSynapseClassInDOM(this.isWidgetOnFullscreen(),  ConfigService.getElementClassOrIdToHideInFullScreenMode(),  ConfigService.getElementClassOrIdToHideInFullScreenModePrefixed());
        BotInitializationService.toggleSynapseClassInDOM(this.isWidgetOnFullscreen(),  "body",  ConfigService.getSynapseClassToHideScrollbar());
      });
      EventBus.$on('ON_CLOSE_CONVERSATION_PANEL', () => this.panelIsOpen = false);

      this.isFullscreenDisplay = ResponsiveUiHelper.isFullscreenDisplay(this.$vuetify);
      BotInitializationService.toggleSynapseClassInDOM(false,  ConfigService.getElementClassOrIdToHideInFullScreenMode(),  ConfigService.getElementClassOrIdToHideInFullScreenModePrefixed());
      BotInitializationService.toggleSynapseClassInDOM(false,  "body",  ConfigService.getSynapseClassToHideScrollbar());

      this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      this.isReduced = false;
      this.showReduceButton = false;

      if ((this.reducedAvatarPlatfom === 'all' || this.reducedAvatarPlatfom === 'desktop') && !this.isMobile) {
        // In desktop mode, the avatar we display the reduce button
        this.showReduceButton = true;
        // Check in storage if the avatar is reduced
        this.isReduced = sessionStorage.getItem('avatarReduced') === 'true';
      } else if ((this.reducedAvatarPlatfom === 'all' || this.reducedAvatarPlatfom === 'mobile') && this.isMobile) {
        // In mobile mode, the avatar we don't display the reduce button
        this.showReduceButton = false;
        // Avatar is reduced by default
        this.isReduced = true;
      }
      else if (this.reducedAvatarPlatfom === 'none') {
        // In none mode, the avatar we don't display the reduce button and the avatar is not reduced
        this.showReduceButton = false;
      }
    }

    mounted() {
      // initialize avatar animations
      const welcomeAvatar: string = ConfigThemeService.getWelcomeAvatarAnimation();
      if(welcomeAvatar.indexOf('bounce') !== -1) {
        this.playWelcomeAvatarBounceAnimation = true;
      }
      if(welcomeAvatar.indexOf('slide') !== -1) {
        this.playWelcomeAvatarSlideAnimation = true;
      }
      if(welcomeAvatar.indexOf('fade') !== -1) {
        this.playWelcomeAvatarFadeAnimation = true;
      }
      
      // Add event to open chatbot on key 'enter'
      this.$el.addEventListener('keydown', (event) => {
        if ((event as KeyboardEvent).key === 'Enter') {
          this.openConversationPanel();
        }
      });

      EventBus.$emit('ON_WIDGET_LOADED_EVT');
    }

    destroyed() {
      EventBus.$off('ON_DRAWER_OPEN');
      EventBus.$off('ON_DRAWER_CLOSE');
      EventBus.$off('ON_RESIZE_SCREEN_MODE');
      EventBus.$off('ON_CLOSE_CONVERSATION_PANEL');
    }

    openConversationPanel() {
      if (this.getUnseenNotifications && this.getUnseenNotifications.length > 0){
        this.markNotificationAsSeen({notif : this.getUnseenNotifications[0] , sessionInfo : this.conversationSessionInfo})
      }
      this.setConversationPanelState(true);
      this.panelIsOpen = true;
      BotInitializationService.toggleSynapseClassInDOM(this.isWidgetOnFullscreen(),  ConfigService.getElementClassOrIdToHideInFullScreenMode(),  ConfigService.getElementClassOrIdToHideInFullScreenModePrefixed());
      BotInitializationService.toggleSynapseClassInDOM(this.isWidgetOnFullscreen(),  "body",  ConfigService.getSynapseClassToHideScrollbar());

      // Read the last message
      const elements = document.querySelectorAll('[id="str-msg-bot-content"]');
      const lastElement = elements.item(elements.length - 1);
      if (lastElement) {
          lastElement.setAttribute('aria-hidden', 'false');
      }
    }

    reduceAvatar() {
      this.isReduced = true;
      sessionStorage.setItem('avatarReduced', 'true');
    }
    
    /** ******************************************************************************************* */
    /**                                   TITTLE FOR BUTTONS                                        */
    /** ******************************************************************************************* */

    get openConversationPanelButtonTitle() {
      return ConfigService.getOpenButtonTitle();
    }

    get openNotificationButtonTitle() {
      return ConfigService.getOpenNotificationButtonTitle();
    }

  }
